import { PowerSettingsNew as PowerIcon } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { getDeployEnv, PDF_DASHBOARD_URL } from "../../../../../environment.js";
import { useUserData } from "../../../../auth/useUserData.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import { useCardDirtyRegistry } from "./integrationCardForm.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { TemplateLetterheadsCard } from "./TemplateLetterheadsCard.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";

export const PdfGeneratorSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const meta = getIntegrationMeta(integrationId);
  const viewer = useUserData().currentUser!;
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const { anyDirty, dirtyOf } = useCardDirtyRegistry();
  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  const integrationName = meta.sidebarLabel ?? "PDF Generator";

  if (mode === "create") {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName="PDF Generator"
        title={`${t("Connect")} PDF Generator`}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: meta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              {meta.shortName}
            </Typography>
          </Box>
          <Typography variant="h6" gutterBottom>
            PDF Generator
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 420, textAlign: "center" }}
          >
            {t(meta.description as any)}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PowerIcon />}
            onClick={async () => {
              try {
                await handleAdd();
              } catch (e) {
                enqueueSnackbar(
                  integrationErrorMessage(
                    e,
                    t("Settings could not be saved. Please check your input.")
                  ),
                  { variant: "error" }
                );
              }
            }}
          >
            {t("Connect")}
          </Button>
        </Box>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
    >
      <TemplateLetterheadsCard
        onDirtyChange={dirtyOf("templateLetterheads")}
        storedSettings={orgIntegration?.settings}
        templatesDashboardUrl={templatesDashboardUrl(viewer.id)}
        onSave={async assignments => {
          try {
            await handleUpdate({ templateLetterheads: assignments });
            await refetchDashboard();
          } catch (e) {
            enqueueSnackbar(
              integrationErrorMessage(
                e,
                t("Settings could not be saved. Please check your input.")
              ),
              { variant: "error" }
            );
          }
        }}
      />
    </SettingsPageLayout>
  );
};

// `environment` must be the env the app is actually running in. It is not a
// build-time constant — since MEIS-12358 the env-specific values arrive at
// runtime from `/config.json`, and only the `static.*` block is baked into the
// bundle. Reading `import.meta.env.VITE_DEPLOY_ENV` here would bake whatever
// `apps/frontend/.env` holds at build time and point every deployment at the
// wrong dashboard environment.
function templatesDashboardUrl(viewerId: string): string {
  const url = new URL(PDF_DASHBOARD_URL);
  url.searchParams.set("environment", getDeployEnv());
  url.searchParams.set("selectedUserId", viewerId);
  url.searchParams.set("initialPath", "/dashboard");
  return url.toString();
}
